import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@repletoryreach/sdk-auth';
import { identity } from '@repletoryreach/sdk-api';
import { Stack, Card, Title, Button, Text } from '@repletoryreach/sdk-ui';
import { PasswordInput } from '@repletoryreach/sdk-ui/inputs';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { TwoFactorPanel } from './components/TwoFactorPanel';
import { SessionsPanel } from './components/SessionsPanel';
import { SignInMethodsCard, SetPasswordCard, SIGN_IN_METHODS_KEY } from './components/SignInMethodsCard';
import { PasswordRules } from '../../../app/components/PasswordRules';
import { isPasswordValid, passwordRefusalMessage } from '../../../lib/password';
import './security.css';
import { useT } from '../../../i18n/I18nProvider';

export default function SettingsSecurityPage() {
  const t = useT();
  const { changePassword } = useAuth();
  const navigate = useNavigate();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const valid = isPasswordValid(next);
  // Someone who signed up with Google has no password to change: they are offered a first one instead.
  const methods = useQuery({ queryKey: SIGN_IN_METHODS_KEY, queryFn: () => identity.getSignInMethods() });
  const hasPassword = methods.data?.data.has_password ?? true;

  async function submitChange() {
    setError(null);
    if (!valid) { setError(t('auth.reset.errors.weak')); return; }
    if (next !== confirm) { setError(t('auth.reset.errors.mismatch')); return; }
    setBusy(true);
    try {
      await changePassword(current, next);
      setCurrent(''); setNext(''); setConfirm('');
      notifySuccess(t('settings.security.toast.changed'), t('settings.nav.security'));
    } catch (e) {
      setError(passwordRefusalMessage(e, t) ?? (e instanceof Error ? e.message : t('settings.security.changeFailed')));
    } finally {
      setBusy(false);
    }
  }


  return (
    <Stack gap="md">
      {!hasPassword ? <SetPasswordCard /> : (
      <Card withBorder data-guide="security-password">
        <Stack gap="md">
          <Title order={4}>{t('settings.security.changePassword')}</Title>
          <PasswordInput label={t('settings.security.currentPassword')} value={current} onChange={(e) => setCurrent(e.currentTarget.value)} autoComplete="current-password" />
          <PasswordInput label={t('auth.reset.form.newPassword')} value={next} onChange={(e) => setNext(e.currentTarget.value)} autoComplete="new-password" />
          {/* §4 item 7: this page enforced length ≥ 8 while the Team page
              printed a stricter policy, and neither matched the service. One
              component, one rule set, mirroring `services/identity`. */}
          {next.length > 0 && <PasswordRules value={next} />}
          <PasswordInput label={t('settings.security.confirmNewPassword')} value={confirm} onChange={(e) => setConfirm(e.currentTarget.value)} autoComplete="new-password" />
          {error && <Alert color="error">{error}</Alert>}
          <div>
            {/* §4 item 6: `confirm` was not in the guard, so the button was
                enabled with an empty confirmation and the mismatch only
                appeared after clicking. */}
            <Button onClick={submitChange} loading={busy} disabled={!current || !valid || !confirm}>
              {t('settings.security.changePassword')}
            </Button>
          </div>
        </Stack>
      </Card>
      )}

      <SignInMethodsCard />

      <TwoFactorPanel />

      <SessionsPanel />
    </Stack>
  );
}
